import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  checkGmailAuth,
  getGmailAuthUrl,
  authorizeGmail,
  fetchInbox,
  fetchEmail,
  markEmailAsRead,
  clearError,
  clearCurrentEmail
} from '../store/slices/gmailSlice';
import { toast } from 'react-toastify';
import {
  EnvelopeIcon,
  EnvelopeOpenIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  EyeIcon,
  ClockIcon,
  UserIcon
} from '@heroicons/react/24/outline';

const GmailInbox = () => {
  const dispatch = useDispatch();
  const {
    isAuthorized,
    authUrl,
    emails,
    currentEmail,
    loading,
    error,
    total
  } = useSelector((state) => state.gmail);
  const { user } = useSelector((state) => state.auth);

  const [authCode, setAuthCode] = useState('');
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (user?.role === 'superadmin') {
      dispatch(checkGmailAuth());
    }
  }, [dispatch, user]);

  useEffect(() => {
    if (isAuthorized) {
      dispatch(fetchInbox({ maxResults: 50 }));
    }
  }, [dispatch, isAuthorized]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const handleAuthorize = async () => {
    if (!authCode.trim()) {
      toast.error('Please enter the authorization code');
      return;
    }

    const result = await dispatch(authorizeGmail(authCode));
    if (result.type === 'gmail/authorize/fulfilled') {
      toast.success('Gmail authorized successfully!');
      setShowAuthModal(false);
      setAuthCode('');
      dispatch(fetchInbox({ maxResults: 50 }));
    }
  };

  const handleGetAuthUrl = async () => {
    await dispatch(getGmailAuthUrl());
    setShowAuthModal(true);
  };

  const handleEmailClick = async (email) => {
    setSelectedEmail(email);
    await dispatch(fetchEmail(email.id));

    if (!email.isRead) {
      await dispatch(markEmailAsRead(email.id));
    }
  };

  const handleRefresh = () => {
    dispatch(fetchInbox({ maxResults: 50, query: searchQuery }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    dispatch(fetchInbox({ maxResults: 50, query: searchQuery }));
  };

  const formatDate = (timestamp) => {
    return new Date(parseInt(timestamp)).toLocaleString();
  };

  const getEmailPreview = (body, maxLength = 100) => {
    if (!body) return '';
    const text = body.replace(/<[^>]*>/g, ''); // Remove HTML tags
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  // Only superadmin can access Gmail
  if (user?.role !== 'superadmin') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-500" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">Only Super Administrators can access Gmail inbox.</p>
        </div>
      </div>
    );
  }

  if (!isAuthorized) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8">
          <div className="text-center">
            <div className="bg-gradient-to-br from-blue-500 to-indigo-600 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
              <EnvelopeIcon className="h-10 w-10 text-white" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-3">Gmail Authorization Required</h2>
            <p className="text-gray-600 mb-8 text-base">
              To access Gmail inbox, you need to authorize the application to access your Gmail account.
            </p>
            <button
              onClick={handleGetAuthUrl}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 px-6 rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl font-semibold"
            >
              Authorize Gmail Access
            </button>
          </div>
        </div>

        {/* Authorization Modal */}
        {showAuthModal && (
          <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-6 sm:p-8 max-w-md w-full shadow-2xl">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Gmail Authorization</h3>
              <div className="space-y-5">
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-3">
                    1. Click the link below to authorize Gmail access:
                  </p>
                  <a
                    href={authUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block text-blue-600 hover:text-blue-800 underline break-all font-medium"
                  >
                    Open Gmail Authorization
                  </a>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-3">
                    2. Copy the authorization code and paste it here:
                  </p>
                  <input
                    type="text"
                    value={authCode}
                    onChange={(e) => setAuthCode(e.target.value)}
                    placeholder="Paste authorization code here"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                  />
                </div>
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={handleAuthorize}
                    disabled={loading}
                    className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 px-4 rounded-xl hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 font-semibold transition-all duration-200 shadow-lg"
                  >
                    {loading ? 'Authorizing...' : 'Authorize'}
                  </button>
                  <button
                    onClick={() => setShowAuthModal(false)}
                    className="flex-1 bg-gray-100 text-gray-700 py-3 px-4 rounded-xl hover:bg-gray-200 font-semibold transition-all duration-200"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">Gmail Inbox</h1>
          <p className="mt-2 text-sm sm:text-base text-gray-600 font-medium">
            Manage incoming emails ({total} emails)
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={loading}
          className="flex items-center justify-center px-5 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105 font-semibold"
        >
          <ArrowPathIcon className={`h-5 w-5 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Search */}
      <div className="bg-white p-4 sm:p-5 rounded-2xl shadow-xl border border-gray-200">
        <form onSubmit={handleSearch} className="flex gap-3">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search emails..."
            className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
          />
          <button
            type="submit"
            className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-xl hover:from-blue-700 hover:to-indigo-700 font-semibold transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            Search
          </button>
        </form>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Email List */}
        <div className="lg:col-span-2">
          <div className="bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-200">
            <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-900">Emails</h2>
            </div>

            {loading && emails.length === 0 ? (
              <div className="flex justify-center items-center h-64">
                <ArrowPathIcon className="h-8 w-8 animate-spin text-blue-500" />
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {emails.map((email) => (
                  <div
                    key={email.id}
                    onClick={() => handleEmailClick(email)}
                    className={`p-4 sm:p-5 hover:bg-blue-50 cursor-pointer transition-colors duration-150 ${selectedEmail?.id === email.id ? 'bg-blue-50 border-l-4 border-blue-600' : ''
                      } ${!email.isRead ? 'bg-blue-25' : ''}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          {email.isRead ? (
                            <EnvelopeOpenIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
                          ) : (
                            <EnvelopeIcon className="h-5 w-5 text-blue-600 flex-shrink-0" />
                          )}
                          <p className={`text-sm ${!email.isRead ? 'font-bold' : 'font-semibold'} text-gray-900 truncate`}>
                            {email.from}
                          </p>
                        </div>
                        <p className={`text-sm sm:text-base ${!email.isRead ? 'font-bold' : 'font-medium'} text-gray-900 mt-2`}>
                          {email.subject || '(No Subject)'}
                        </p>
                        <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                          {getEmailPreview(email.snippet)}
                        </p>
                      </div>
                      <div className="flex-shrink-0 text-right">
                        <p className="text-xs text-gray-500 font-medium">
                          {formatDate(email.timestamp)}
                        </p>
                        {email.hasAttachments && (
                          <div className="mt-2">
                            <span className="inline-flex items-center px-2 py-1 rounded-lg text-xs font-bold bg-gray-100 text-gray-700">
                              📎
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {emails.length === 0 && !loading && (
              <div className="text-center py-16">
                <div className="bg-gray-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <EnvelopeIcon className="h-10 w-10 text-gray-400" />
                </div>
                <p className="text-gray-600 font-medium">No emails found.</p>
              </div>
            )}
          </div>
        </div>

        {/* Email Content */}
        <div className="lg:col-span-1">
          <div className="bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-200 sticky top-6">
            <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-900">Email Details</h2>
            </div>

            {currentEmail ? (
              <div className="p-6 max-h-[calc(100vh-200px)] overflow-y-auto">
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">
                      {currentEmail.subject || '(No Subject)'}
                    </h3>
                  </div>

                  <div className="flex items-center text-sm text-gray-700 bg-gray-50 p-3 rounded-xl">
                    <UserIcon className="h-5 w-5 mr-2 text-blue-600" />
                    <span className="font-medium">{currentEmail.from}</span>
                  </div>

                  <div className="flex items-center text-sm text-gray-700 bg-gray-50 p-3 rounded-xl">
                    <ClockIcon className="h-5 w-5 mr-2 text-blue-600" />
                    <span className="font-medium">{formatDate(currentEmail.timestamp)}</span>
                  </div>

                  <div className="border-t-2 border-gray-200 pt-4">
                    <div
                      className="prose prose-sm max-w-none text-gray-700"
                      dangerouslySetInnerHTML={{
                        __html: currentEmail.body || currentEmail.snippet
                      }}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="bg-gray-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <EyeIcon className="h-10 w-10 text-gray-400" />
                  </div>
                  <p className="text-gray-600 font-medium">Select an email to view details</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GmailInbox;